import { onError } from '@apollo/client/link/error'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useEffect } from 'react'
import { hiddenInWaveRejectionMessage } from './waveVisibilityErrors'

type Listener = (message: string) => void

const listeners = new Set<Listener>()

/**
 * The backend refuses most questionnaire edits that would leave a visible entry
 * depending on one hidden in this wave (routing, looping, display logic,
 * masking, piping). The hooks behind those edits only log errors, so without
 * this the rejected edit would quietly vanish on the next refetch. The hide
 * action itself reports its own errors.
 */
export const hiddenInWaveRejectionLink = onError(
  ({ graphQLErrors, operation }) => {
    if (operation.operationName === 'setEntriesHiddenV2') {
      return
    }
    const message = graphQLErrors
      ?.map((error) => hiddenInWaveRejectionMessage(error.message))
      .find(Boolean)
    if (message) {
      listeners.forEach((listener) => listener(message))
    }
  }
)

/** Shows those rejections as error snackbars. Mount once, under the SnackbarProvider. */
export const useHiddenInWaveRejectionSnackbar = () => {
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    const listener: Listener = (message) =>
      enqueueSnackbar(message, { variant: SnackbarVariant.Error })
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [enqueueSnackbar])
}
